import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@/lib/apiClient";
import { DEFAULT_PAGE_SIZE } from "@/lib/paging";
import { LIST_PARAMS, useUrlParam } from "@/hooks/useUrlParam";
import { useRememberListUrl } from "@/hooks/useBackTo";
import { useRestoreListScroll } from "@/hooks/useListScroll";

export interface InfinitePage<T> {
  items: T[];
  totalCount: number;
}

export interface InfiniteListParams {
  keyword: string;
  pageNumber: number;
  pageSize: number;
}

export interface InfiniteListState<T, P> {
  items: T[];
  meta: P | null;
  keyword: string;
  setKeyword: (keyword: string) => void;
  loading: boolean;
  loadingMore: boolean;
  loaded: boolean;
  error: string | null;
  forbidden: boolean;
  totalCount: number;
  hasMore: boolean;
  sentinelRef: (node: HTMLElement | null) => void;
  removeItem: (id: string) => void;
  reload: () => void;
}

export function useInfiniteList<T extends { id: string }, P extends InfinitePage<T>>(
  fetcher: (params: InfiniteListParams) => Promise<P>,
  opts: { pageSize?: number; rootMargin?: string; syncUrl?: boolean } = {},
): InfiniteListState<T, P> {
  const pageSize = opts.pageSize ?? DEFAULT_PAGE_SIZE;
  const rootMargin = opts.rootMargin ?? "300px";

  const [items, setItems] = useState<T[]>([]);
  const [meta, setMeta] = useState<P | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [localKeyword, setLocalKeyword] = useState("");
  const [urlKeyword, setUrlKeyword] = useUrlParam(LIST_PARAMS.keyword, "");
  const keyword = opts.syncUrl ? urlKeyword : localKeyword;
  useRememberListUrl(opts.syncUrl === true);
  const setKeyword = opts.syncUrl ? setUrlKeyword : setLocalKeyword;
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  useRestoreListScroll(loaded, opts.syncUrl === true);

  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;
  const requestId = useRef(0);

  const load = useCallback(
    async (search: string, from: number) => {
      const id = requestId.current + 1;
      requestId.current = id;
      if (from === 0) {
        setLoading(true);
        setError(null);
        setForbidden(false);
      } else {
        setLoadingMore(true);
      }
      try {
        const pageNumber = Math.floor(from / pageSize) + 1;
        const res = await fetchRef.current({ keyword: search, pageNumber, pageSize });
        if (id !== requestId.current) return;
        const fresh = res.items.slice(from - (pageNumber - 1) * pageSize);
        setItems((prev) => (from === 0 ? fresh : [...prev, ...fresh]));
        setTotalCount(res.totalCount);
        setMeta(res);
        setLoaded(true);
      } catch (e) {
        if (id !== requestId.current) return;
        if (from === 0) {
          setItems([]);
          setTotalCount(0);
        }
        if (e instanceof ApiError && e.status === 403) setForbidden(true);
        else setError(e instanceof Error ? e.message : "Failed to load the list.");
        setLoaded(true);
      } finally {
        if (id === requestId.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [pageSize],
  );

  useEffect(() => {
    void load(keyword.trim(), 0);
  }, [keyword, load]);

  const hasMore = loaded && items.length < totalCount;

  const loadMore = useRef(() => {});
  loadMore.current = () => {
    if (loading || loadingMore || !hasMore) return;
    void load(keyword.trim(), items.length);
  };

  const observer = useRef<IntersectionObserver | null>(null);
  const node = useRef<HTMLElement | null>(null);

  const observe = useCallback(() => {
    observer.current?.disconnect();
    if (!node.current) return;
    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore.current();
      },
      { rootMargin },
    );
    observer.current.observe(node.current);
  }, [rootMargin]);

  const sentinelRef = useCallback(
    (next: HTMLElement | null) => {
      node.current = next;
      observe();
    },
    [observe],
  );

  useEffect(() => {
    if (!loading && !loadingMore) observe();
  }, [loading, loadingMore, items.length, observe]);

  useEffect(() => () => observer.current?.disconnect(), []);

  return {
    items,
    meta,
    keyword,
    setKeyword,
    loading,
    loadingMore,
    loaded,
    error,
    forbidden,
    totalCount,
    hasMore,
    sentinelRef,
    removeItem: useCallback((id: string) => {
      setItems((prev) => (prev.some((i) => i.id === id) ? prev.filter((i) => i.id !== id) : prev));
      setTotalCount((c) => Math.max(0, c - 1));
    }, []),
    reload: useCallback(() => {
      void load(keyword.trim(), 0);
    }, [load, keyword]),
  };
}
